/* eslint jsx-a11y/mouse-events-have-key-events: 'off', @typescript-eslint/no-explicit-any: 'off' */
import { useContext, useEffect } from 'react';
import { ParentSize } from '@visx/responsive';
import DataContext from "../context/DataContext.js";
import useEventEmitter from "../hooks/useEventEmitter.js";
import EventEmitterProvider from "../providers/EventEmitterProvider.js";
import TooltipContext from "../context/TooltipContext.js";
import TooltipProvider from "../providers/TooltipProvider.js";
import DataProvider from "../providers/DataProvider.js";
import useEventEmitters from "../hooks/useEventEmitters.js";
import { XYCHART_EVENT_SOURCE } from "../constants.js";
import useEventHandlers, { POINTER_EVENTS_ALL, POINTER_EVENTS_NEAREST } from "../hooks/useEventHandlers.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_MARGIN = {
  top: 50,
  right: 50,
  bottom: 50,
  left: 50
};
const allowedEventSources = [XYCHART_EVENT_SOURCE];
export default function XYChart(props) {
  const {
    accessibilityLabel = 'XYChart',
    captureEvents = true,
    children,
    height,
    horizontal,
    margin = DEFAULT_MARGIN,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    pointerEventsDataKey = 'nearest',
    theme,
    width,
    xScale,
    yScale,
    // pass prop to context so it can be used anywhere
    resizeObserverPolyfill: resizeObserverPolyfillProp
  } = props;
  const {
    setDimensions,
    resizeObserverPolyfill
  } = useContext(DataContext);
  const tooltipContext = useContext(TooltipContext);
  const emit = useEventEmitter();

  // update dimensions in context
  useEffect(() => {
    if (setDimensions && width != null && height != null && width > 0 && height > 0) {
      setDimensions({
        width,
        height,
        margin
      });
    }
  }, [setDimensions, width, height, margin]);
  const eventEmitters = useEventEmitters({
    source: XYCHART_EVENT_SOURCE
  });
  useEventHandlers({
    dataKey: pointerEventsDataKey === 'nearest' ? POINTER_EVENTS_NEAREST : POINTER_EVENTS_ALL,
    onPointerMove,
    onPointerOut,
    onPointerUp,
    onPointerDown,
    allowedSources: allowedEventSources
  });

  // if Context or dimensions are not available, wrap self in the needed providers
  if (!setDimensions) {
    if (!xScale || !yScale) {
      console.warn('[@visx/xychart] XYChart: When no DataProvider is available in context, you must pass xScale & yScale config to XYChart.');
      return null;
    }
    return /*#__PURE__*/_jsx(DataProvider, {
      xScale: xScale,
      yScale: yScale,
      theme: theme,
      initialDimensions: {
        width,
        height,
        margin
      },
      horizontal: horizontal,
      resizeObserverPolyfill: resizeObserverPolyfillProp,
      children: /*#__PURE__*/_jsx(XYChart, {
        ...props
      })
    });
  }
  if (width == null || height == null) {
    return /*#__PURE__*/_jsx(ParentSize, {
      resizeObserverPolyfill: resizeObserverPolyfill,
      children: dims => /*#__PURE__*/_jsx(XYChart, {
        ...props,
        width: props.width == null ? dims.width : props.width,
        height: props.height == null ? dims.height : props.height
      })
    });
  }
  if (tooltipContext == null) {
    return /*#__PURE__*/_jsx(TooltipProvider, {
      children: /*#__PURE__*/_jsx(XYChart, {
        ...props
      })
    });
  }

  // EventEmitterProvider should be the last wrapper so we do not duplicate handlers
  if (emit == null) {
    return /*#__PURE__*/_jsx(EventEmitterProvider, {
      children: /*#__PURE__*/_jsx(XYChart, {
        ...props
      })
    });
  }
  if (width <= 0 || height <= 0) {
    if (process.env.NODE_ENV === 'development') {
      console.info('XYChart has a zero width or height, bailing', {
        width,
        height
      });
    }
    return null;
  }
  return /*#__PURE__*/_jsxs("svg", {
    width: width,
    height: height,
    "aria-label": accessibilityLabel,
    children: [children, captureEvents && /*#__PURE__*/_jsx("rect", {
      x: margin.left,
      y: margin.top,
      width: width - margin.left - margin.right,
      height: height - margin.top - margin.bottom,
      fill: "transparent",
      ...eventEmitters
    })]
  });
}